import { BaseApi } from '../base-impl';
import { RequestContext } from '../../api';
import { app } from '../../api/app';
import { calc } from '../../api/calc';
import { communication } from '../../api/communication';
import { document } from '../../api/document';
import { infotrack } from '../../api/infotrack';
import { lawconnect } from '../../api/lawconnect';
import { matter } from '../../api/matter';
import { msteams } from '../../api/msteams';
import { register } from '../../api/register';
import { system } from '../../api/system';
import { accounting } from '../../api/accounting';
import { schema } from '../../api/schema';
import { card } from '../../api/card';
import { staff } from '../../api/staff';
import { person } from '../../api/person';

import { Accounting } from './accounting';
import { App } from './app';
import { Calc } from './calc';
import { Communication } from './communication';
import { Document } from './document';
import { Infotrack } from './infotrack';
import { LawConnect } from './lawconnect';
import { Matter } from './matter';
import { MsTeams } from './msteams';
import { Register } from './register';
import { Schema } from './schema';
import { System } from './system';
import { Card } from './card';
import { Staff } from './staff';
import { Person } from './person';

import { Base64 } from 'js-base64';

export class BrowserFourdApi extends BaseApi {
  private _accounting: accounting.Api;
  private _app: app.Api;
  private _calc: calc.Api;
  private _communication: communication.Api;
  private _document: document.Api;
  private _infotrack: infotrack.Api;
  private _lawconnect: lawconnect.Api;
  private _person: person.Api;
  private _matter: matter.Api;
  private _msteams: msteams.Api;
  private _register: register.Api;
  private _schema: schema.Api;
  private _system: system.Api;
  private _card: card.Api;
  private _staff: staff.Api;

  get accounting(): accounting.Api {
    return this._accounting;
  }
  get app(): app.Api {
    return this._app;
  }
  get calc(): calc.Api {
    return this._calc;
  }
  get card(): card.Api {
    return this._card;
  }
  get communication(): communication.Api {
    return this._communication;
  }
  get document(): document.Api {
    return this._document;
  }
  get infotrack(): infotrack.Api {
    return this._infotrack;
  }
  get lawconnect(): lawconnect.Api {
    return this._lawconnect;
  }
  get matter(): matter.Api {
    return this._matter;
  }
  get msteams(): msteams.Api {
    return this._msteams;
  }
  get person(): person.Api {
    return this._person;
  }
  get register(): register.Api {
    return this._register;
  }
  get schema(): schema.Api {
    return this._schema;
  }
  get system(): system.Api {
    return this._system;
  }
  get staff(): staff.Api {
    return this._staff;
  }
  readonly protocol: string = 'Fourd://';

  constructor(requestContext: RequestContext = undefined) {
    super(requestContext);

    this._accounting = new Accounting(this);
    this._app = new App(this);
    this._calc = new Calc(this);
    this._communication = new Communication(this);
    this._document = new Document(this);
    this._infotrack = new Infotrack(this);
    this._lawconnect = new LawConnect(this);
    this._person = new Person(this);
    this._matter = new Matter(this);
    this._msteams = new MsTeams(this);
    this._register = new Register(this);
    this._schema = new Schema(this);
    this._system = new System(this);
    this._card = new Card(this);
    this._staff = new Staff(this);

    this.setupHostMessageEventHandler();
  }

  protected retrieveContext(): void {
    this.call4Dv2('RetrieveLeapAppContext', {}, this.leapContext.hostInfo.appSessionId);
  }

  to4DJSONString(value: any, encode?: boolean): string {
    if (encode === undefined) {
      return 'json=' + Base64.encode(JSON.stringify(value));
    } else {
      return JSON.stringify(value);
    }
  }

  call4D(request: string): void {
    if (!window['$4d']) {
      //this object is lost when the permission acceptance page pops up
      //so we need to handle it in the old way
      request = this.protocol + request;
      window.location.assign(request);
    } else {
      request = this.protocol + request;
      window['$4d'].WebArea_RequestHandler(request);
    }
  }

  call4Dv2(command: string, request?: any, requestId?: string): void {
    if (request === undefined || typeof request === 'object') {
      // Always append appSessionId to the request sent to Host for validation
      request = {
        ...(request || {}),
        appSessionId: this.leapContext.hostInfo.appSessionId,
      };
      if (requestId) {
        request = { ...request, requestId };
      }
    }

    const obj = {
      command,
      request,
      sdkVersion: 2, //keep this 2 for now so it wont break LS
    };
    this.call4D(this.to4DJSONString(obj));
  }

  protected hostRelatedSetup(): void {}

  // addMouseMoveListener is a web-only concern; no-op on desktop.
  public addMouseMoveListener(): void {}

  public internalSendAsync(request: any): Promise<any> {
    if (request === null || request === undefined) {
      throw new Error('Bad request');
    }

    const isObject = typeof request === 'object';

    if (isObject && request['command']) {
      // Proxy generic command use case
      return this.twoWayCall<any, any>(
        request,
        (requestId, request) => {
          this.call4Dv2(request['command'], request['request'], requestId);
        },
        (response) => {
          return response;
        }
      );
    } else {
      // For strings
      return this.twoWayCall<any, any>(
        request,
        (requestId, request) => {
          this.call4Dv2('system.send', request, requestId);
        },
        (response) => {
          return response;
        }
      );
    }
  }

  public internalSend(request: any): void {
    if (request === null || request === undefined) {
      throw new Error('Bad request');
    }

    const isObject = typeof request === 'object';

    if (isObject && request['command']) {
      // Proxy generic command use case
      this.call4Dv2(request['command'], request['request']);
    } else {
      // For strings
      this.call4Dv2('system.send', request);
    }
  }

  private setupHostMessageEventHandler() {
    const global = window as any;
    // If SKD has registered the command function, LEAP Desktop will be able to execute it
    global.handleHostMessage = (command: string, payload?: string) => super.handleCommand(command, payload);
  }
}
